import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { CONTACT_STATUS_OPTIONS, CONTACT_SOURCE_OPTIONS } from "@/lib/schemas/contact";
import { TAG_SETS } from "@/components/ui/tags/CommonTagChips";
import { CheckCircle, Settings, Tag, TrendingUp } from "lucide-react";

// Status and tracking fields: status, source, tags, is_active, lifecycle_stage
export const statusContactFields = defineFieldConfigMap({
  // FIELD: status
  status: {
    type: "select",
    label: "Status",
    isStatusField: true,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    bulkEditable: true, // ✅ Enable bulk editing
    filterType: "exact",
    width: 130,
    minWidth: 100,
    maxWidth: 150,
    options: CONTACT_STATUS_OPTIONS.map((option) => ({
      label: option.label,
      value: option.value,
    })),
    placeholder: "Select status",
    icon: "CheckCircle",
    groupable: true,
    // Role-based access control - admin only for status changes
    allowedRoles: ["wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "status",
    graphqlSelector: "status",
    savedViewMembership: [
      "all-contacts",
      "active-contacts",
      "no-tickets-this-month",
      "with-tickets-this-month",
      "no-tickets-last-month",
      "with-tickets-last-month",
      "needs-follow-up",
      "cold-90-days",
    ],
  },
  // FIELD: source
  source: {
    type: "select",
    label: "Source",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    filterType: "exact",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    options: CONTACT_SOURCE_OPTIONS.map((option) => ({
      label: option.label,
      value: option.value,
    })),
    placeholder: "Select source",
    icon: "Settings",
    groupable: true,
    // Role-based access control - admin only for source tracking
    allowedRoles: ["wtc_admin"],
    mobileDetailSectionId: "status",
    graphqlSelector: "source",
    savedViewMembership: ["leads", "prospects"],
  },
  // FIELD: tags
  tags: {
    type: "tags",
    label: "Tags",
    sortable: false,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 3,
    bulkEditable: true, // ✅ Enable bulk editing
    filterType: "contains",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    tagsConfig: {
      commonTags: TAG_SETS.CONTACT || [],
      maxTags: 10,
      allowCustom: true,
    },
    placeholder: "Add tags",
    icon: "Tag",
    groupable: true,
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "status",
    graphqlSelector: "tags",
  },
  // FIELD: is_active
  is_active: {
    type: "boolean",
    label: "Active",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    bulkEditable: true, // ✅ Enable bulk editing
    filterType: "exact",
    width: 100,
    minWidth: 80,
    maxWidth: 120,
    icon: "CheckCircle",
    groupable: true,
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "status",
    graphqlSelector: "is_active",
    savedViewMembership: ["all-contacts"],
  },
  // FIELD: lifecycle_stage
  lifecycle_stage: {
    type: "text",
    label: "Lifecycle Stage",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    filterType: "contains",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    placeholder: "e.g., subscriber, lead, customer",
    icon: "TrendingUp",
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "status",
    graphqlSelector: "lifecycle_stage",
  },
});
